import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';

import { Target } from '../../../client';
import useMergeProps from '../../../hooks/useMergeProps';
import { SoundIds } from '../../../types/sounds';
import useGRWelcomeAnimationUtils from '../../gift-race/hooks/useGRWelcomeAnimationUtils';
import WelcomeAnimation from '../components/common/WelcomeAnimation';
import selectGRWelcomeAnimationProps from '../selectors/specifics/selectGRWelcomeAnimationProps';

const transitionClassNamePrefix = 'boom-welcome-animation-transition';

const APPEARANCE_TIMEOUT = 200;

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const StyledWelcomeAnimation = styled(WelcomeAnimation)`
  &.${transitionClassNamePrefix}-enter {
    animation: ${appearAnim} ${APPEARANCE_TIMEOUT}ms ease-out forwards;
  }

  &.${transitionClassNamePrefix}-exit {
    animation: ${appearAnim} ${APPEARANCE_TIMEOUT}ms ease-out forwards reverse;
  }
`;

type GRWelcomeAnimationContainerProps = {
  className?: string;
};

const GRWelcomeAnimationContainer = (ownProps: GRWelcomeAnimationContainerProps) => {
  const [shouldShow, setHasBeenViewed] = useGRWelcomeAnimationUtils();

  const selectedProps = useSelector(selectGRWelcomeAnimationProps);

  const onAnimationEnd = useCallback(() => {
    setHasBeenViewed(true);
  }, [setHasBeenViewed]);

  const otherProps = useMemo(
    () => ({
      onAnimationEnd,
      soundId: Target.__MOBILE__ ? SoundIds.BOOM_START : undefined,
    }),
    [onAnimationEnd],
  );

  const { animationId, ...enhancedProps } = useMergeProps({ ownProps, otherProps, selectedProps });

  if (animationId) {
    return (
      <CSSTransition
        in={shouldShow}
        timeout={APPEARANCE_TIMEOUT}
        classNames={transitionClassNamePrefix}
        mountOnEnter
        unmountOnExit
      >
        <StyledWelcomeAnimation {...enhancedProps} animationId={animationId} />
      </CSSTransition>
    );
  }
};

export default GRWelcomeAnimationContainer;
